// First-hole "press and drag to shoot" tutorial overlay.
//
// Shown once, ever, on the first hole of a brand-new player's first
// session. Detected via a `localStorage` key so the second round — or
// the second tab, or a return visit — never sees it. The overlay is a
// full-screen, pointer-events:none layer that sits above the 3D canvas
// but below the HUD panels; nothing it renders can intercept input.
//
// Dismiss paths (whichever fires first):
//   - Real pointerdown on the canvas (the user has started aiming)
//   - ~12s timeout
//   - Caller invokes `dispose()` on scene teardown
//
// Motion: a ghost hand drifts in above the press point, presses down,
// and drags straight *down* the screen (matching the real aim mechanic:
// pdy > 0 = shoot forward). While it drags, the tutorial renders visual
// replicas of the real HUD — a cyan joy-outer ring at the press point,
// a joy-knob following the hand, a joy-line connecting them, and the
// bottom power bar filling left-to-right in sync. On release everything
// fades and the ball "shoots" upward (opposite of drag). Then a blank
// beat, then it loops. `prefers-reduced-motion: reduce` collapses to a
// static mid-drag frame.

const SEEN_KEY_BASE = "cosmicpar:tutorial_seen_v1";
const AUTO_DISMISS_MS = 12_000;
const FADE_OUT_MS = 160;

export interface TutorialHandle {
  dispose(): void;
}

export interface TutorialOpts {
  /** Element the real pointerdown-to-dismiss listener attaches to.
   *  Typically the WebGL canvas from `renderer.domElement`. */
  canvas: HTMLElement;
  /** Mode the tutorial is being shown for. Each mode gets its own
   *  seen-flag so a player who learned the mechanic in Practice still
   *  sees the hint the first time they enter Compete, and vice versa. */
  modeKey: "daily" | "endless";
  /** Override for storage check — useful for tests / forced replays. */
  force?: boolean;
}

export function mountFirstHoleTutorial(
  opts: TutorialOpts,
): TutorialHandle {
  const seenKey = `${SEEN_KEY_BASE}:${opts.modeKey}`;
  if (!opts.force && hasSeenBefore(seenKey)) {
    return { dispose() {} };
  }

  injectStylesOnce();

  const root = document.createElement("div");
  root.className = "cp-tutorial";
  root.setAttribute("aria-hidden", "true");
  root.innerHTML = buildMarkup();

  document.body.appendChild(root);

  // Kick the entrance transition on the next frame so the initial
  // opacity:0 state actually paints before the target opacity:1 lands.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      root.classList.add("cp-tutorial--on");
    });
  });

  let disposed = false;
  const autoT = window.setTimeout(() => dismiss(), AUTO_DISMISS_MS);

  const onCanvasDown = () => dismiss();
  opts.canvas.addEventListener("pointerdown", onCanvasDown, {
    passive: true,
    capture: true,
  });

  function dismiss(): void {
    if (disposed) return;
    disposed = true;
    try {
      localStorage.setItem(seenKey, "1");
    } catch {
      /* quota / private mode — no-op */
    }
    clearTimeout(autoT);
    opts.canvas.removeEventListener("pointerdown", onCanvasDown, {
      capture: true,
    } as EventListenerOptions);
    root.classList.add("cp-tutorial--out");
    window.setTimeout(() => {
      root.remove();
    }, FADE_OUT_MS + 40);
  }

  return { dispose: dismiss };
}

function hasSeenBefore(seenKey: string): boolean {
  try {
    return localStorage.getItem(seenKey) === "1";
  } catch {
    // If storage is unavailable (private Safari, etc.) fall back to
    // in-memory — better to show it than to spam a broken overlay.
    return false;
  }
}

// The tutorial mimics the real aim HUD: cyan joy-outer ring at the
// press point, a knob that slides down with the drag, a gradient line
// between them, and a bottom-center power bar that fills in sync. The
// ghost hand is the only "narration" element — everything else is a
// faithful replica of what the player will see when they touch down.
function buildMarkup(): string {
  return `
    <div class="cp-tutorial__stage">
      <!-- Joystick replica, centered on the press point. -->
      <div class="cp-tutorial__joy">
        <div class="cp-tutorial__joy-line"></div>
        <div class="cp-tutorial__joy-outer"></div>
        <div class="cp-tutorial__joy-ripple"></div>
        <div class="cp-tutorial__joy-knob"></div>
      </div>

      <!-- Ghost hand. Starts above the press point, drifts down,
           presses, drags down, flicks off. -->
      <svg class="cp-tutorial__hand" viewBox="0 0 64 84" aria-hidden="true">
        <defs>
          <radialGradient id="cpTutHandGlow" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stop-color="#ffffff" stop-opacity="0.95"/>
            <stop offset="60%" stop-color="#d4e4ff" stop-opacity="0.6"/>
            <stop offset="100%" stop-color="#7cf5ff" stop-opacity="0"/>
          </radialGradient>
        </defs>
        <circle cx="19" cy="30" r="24" fill="url(#cpTutHandGlow)"/>
        <!-- Pointing-hand silhouette: index finger OFFSET LEFT (so it
             doesn't read as a middle finger), with the closed fist
             bulging to the right and a soft thumb-bump on the left.
             Coordinates: finger x=15..23 y=6..34, palm x=8..54 y=34..78. -->
        <path d="M 15 10
                 C 15 6, 23 6, 23 10
                 L 23 34
                 L 48 34
                 C 52 34, 54 38, 54 42
                 L 54 68
                 C 54 75, 48 78, 42 78
                 L 18 78
                 C 12 78, 8 72, 8 66
                 L 8 44
                 C 8 40, 11 36, 15 36
                 L 15 10
                 Z"
              fill="rgba(14, 20, 44, 0.72)"
              stroke="#7cf5ff" stroke-width="1.6"
              stroke-linejoin="round"/>
        <circle cx="19" cy="14" r="2" fill="#ffffff" opacity="0.9"/>
      </svg>

      <!-- Shot-direction burst — a comet shooting UP from the press
           point after release, hinting the ball goes opposite the drag. -->
      <div class="cp-tutorial__shot-burst"></div>
    </div>

    <!-- Power bar replica, positioned identically to the real #hud-power. -->
    <div class="cp-tutorial__power">
      <div class="cp-tutorial__power-fill"></div>
    </div>

    <!-- Caption pill, centered between stage and power bar. -->
    <div class="cp-tutorial__text">
      <span class="cp-tutorial__text-main">Drag to aim</span>
      <span class="cp-tutorial__text-sep">·</span>
      <span class="cp-tutorial__text-sub">release to fire</span>
    </div>
  `;
}

let stylesInjected = false;
function injectStylesOnce(): void {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement("style");
  style.id = "cp-tutorial-style";
  style.textContent = STYLES;
  document.head.appendChild(style);
}

// The whole overlay is a single stacking context floating above the
// canvas (z:30, under settings panels at z:55 and the menu at z:100),
// with pointer-events disabled everywhere. We re-enable nothing — the
// dismiss listener is attached to the canvas itself, not to us.
//
// Drag distance is 120px on desktop, 100px on mobile (tracks the real
// MAX_PIXELS thresholds loosely so the gesture feels proportional).
const STYLES = `
.cp-tutorial {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 30;
  opacity: 0;
  transition: opacity 360ms cubic-bezier(.2,.8,.2,1);
  /* Respect safe-area insets on notched phones. */
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  will-change: opacity;
  --cp-tut-drag: 120px;
  --cp-tut-outer-size: 260px;
  --cp-tut-knob-size: 48px;
}
.cp-tutorial--on { opacity: 1; }
.cp-tutorial--out { opacity: 0; transition-duration: ${FADE_OUT_MS}ms; }

/* Stage wraps the joystick replica + hand. The press point is the
   stage's (50%, 0) anchor; the hand drags down from there. */
.cp-tutorial__stage {
  position: absolute;
  left: 50%;
  top: 45%;
  width: 0;
  height: 0;
  transform: translate(-50%, -50%);
}

/* ---------- Joystick replica ---------- */
/* Wrapper is a 0x0 anchor centered on the press point. */
.cp-tutorial__joy {
  position: absolute;
  left: 0;
  top: 0;
}

.cp-tutorial__joy-outer {
  position: absolute;
  left: 0;
  top: 0;
  width: var(--cp-tut-outer-size);
  height: var(--cp-tut-outer-size);
  margin-left: calc(var(--cp-tut-outer-size) / -2);
  margin-top: calc(var(--cp-tut-outer-size) / -2);
  border: 2px solid rgba(124, 245, 255, 0.5);
  border-radius: 50%;
  box-shadow:
    0 0 32px rgba(124, 245, 255, 0.25),
    inset 0 0 24px rgba(124, 245, 255, 0.12);
  opacity: 0;
  transform: scale(0.5);
  animation: cp-tut-joy-outer 3000ms ease-out infinite;
}

.cp-tutorial__joy-ripple {
  position: absolute;
  left: 0;
  top: 0;
  width: 40px;
  height: 40px;
  margin-left: -20px;
  margin-top: -20px;
  border: 1.5px solid rgba(124, 245, 255, 0.7);
  border-radius: 50%;
  opacity: 0;
  transform: scale(0.4);
  animation: cp-tut-joy-ripple 3000ms ease-out infinite;
}

.cp-tutorial__joy-knob {
  position: absolute;
  left: 0;
  top: 0;
  width: var(--cp-tut-knob-size);
  height: var(--cp-tut-knob-size);
  margin-left: calc(var(--cp-tut-knob-size) / -2);
  margin-top: calc(var(--cp-tut-knob-size) / -2);
  background: radial-gradient(
    circle,
    rgba(180, 253, 255, 0.9) 0%,
    rgba(124, 245, 255, 0.55) 60%,
    rgba(124, 245, 255, 0.15) 100%
  );
  border: 1.5px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  box-shadow: 0 0 24px rgba(124, 245, 255, 0.7);
  opacity: 0;
  transform: translate(0, 0);
  animation: cp-tut-joy-knob 3000ms cubic-bezier(.4,.1,.3,1) infinite;
}

/* Thin gradient bar connecting press center to knob. Starts 3px tall,
   scaleY grows into the drag length, sits halfway down the gap. */
.cp-tutorial__joy-line {
  position: absolute;
  left: 0;
  top: 0;
  width: 3px;
  height: var(--cp-tut-drag);
  margin-left: -1.5px;
  margin-top: 0;
  background: linear-gradient(
    180deg,
    rgba(124, 245, 255, 0.1),
    rgba(124, 245, 255, 0.85)
  );
  border-radius: 999px;
  transform-origin: top center;
  opacity: 0;
  transform: scaleY(0);
  animation: cp-tut-joy-line 3000ms cubic-bezier(.4,.1,.3,1) infinite;
}

/* ---------- Ghost hand ---------- */
.cp-tutorial__hand {
  position: absolute;
  left: 0;
  top: 0;
  width: 56px;
  height: 72px;
  margin-left: -16px;
  margin-top: -12px;
  filter:
    drop-shadow(0 0 10px rgba(124, 245, 255, 0.55))
    drop-shadow(0 6px 12px rgba(8, 10, 24, 0.4));
  animation: cp-tut-hand 3000ms cubic-bezier(.4,.1,.3,1) infinite;
  will-change: transform, opacity;
}

/* ---------- Shot-direction burst ---------- */
/* A small comet that fires UP (opposite of drag) from the press point
   on release — hints that drag-down means shoot-forward. */
.cp-tutorial__shot-burst {
  position: absolute;
  left: 0;
  top: 0;
  width: 10px;
  height: 10px;
  margin-left: -5px;
  margin-top: -5px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(255, 210, 124, 0.85) 50%,
    rgba(255, 210, 124, 0) 100%
  );
  box-shadow:
    0 0 14px rgba(255, 210, 124, 0.8),
    0 0 28px rgba(255, 158, 216, 0.5);
  opacity: 0;
  transform: translate(0, 0) scale(0.4);
  animation: cp-tut-shot-burst 3000ms ease-out infinite;
}

/* ---------- Power bar replica ---------- */
/* Matches #hud-power: fixed, bottom-center, same width/height/colors. */
.cp-tutorial__power {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: max(18px, env(safe-area-inset-bottom));
  width: min(280px, 70vw);
  height: 6px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  overflow: hidden;
}
.cp-tutorial__power-fill {
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #7cf5ff, #ff9ed8);
  transform-origin: left center;
  transform: scaleX(0);
  opacity: 0;
  animation: cp-tut-power 3000ms cubic-bezier(.4,.1,.3,1) infinite;
}

/* ---------- Caption pill ---------- */
.cp-tutorial__text {
  position: absolute;
  left: 50%;
  bottom: calc(max(18px, env(safe-area-inset-bottom)) + 26px);
  transform: translateX(-50%);
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
  padding: 8px 16px;
  background: rgba(10, 12, 24, 0.55);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(124, 245, 255, 0.25);
  border-radius: 999px;
  font-family: ui-sans-serif, system-ui, -apple-system, "SF Pro Text", "Segoe UI", Inter, sans-serif;
  font-size: 11px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #e8ecff;
  white-space: nowrap;
  box-shadow: 0 0 24px rgba(124, 245, 255, 0.15);
  animation: cp-tut-text 3000ms ease-in-out infinite;
}
.cp-tutorial__text-main {
  font-weight: 700;
  color: #7cf5ff;
  text-shadow: 0 0 14px rgba(124, 245, 255, 0.55);
}
.cp-tutorial__text-sep { color: rgba(255, 255, 255, 0.25); }
.cp-tutorial__text-sub { color: #8a8fb3; font-weight: 600; }

/* -------- Keyframes --------
   Loop timing (3000ms total):
     0–10%   Hand drifts in from above. Joystick + power bar hidden.
     10–15%  Hand presses. Joy-outer pops in (0.5 → 0.7), ripple pulses.
             Power bar fades in at scale 0.
     15–60%  Hand drags down. Knob follows, line grows, outer scales up
             toward 1.0, power bar fills to ~0.85.
     60–70%  Release. Hand flicks off (scale bounce + fade). Joystick
             fades out. Power bar flashes to 1.0, then fades. Shot burst
             fires upward from press point.
     70–100% Blank hold. Loop.
*/

@keyframes cp-tut-hand {
  0%   { transform: translate(-18px, -90px) rotate(-6deg) scale(1.02); opacity: 0; }
  8%   { transform: translate(-8px, -50px) rotate(-3deg) scale(1.02); opacity: 0.85; }
  12%  { transform: translate(0, -8px) rotate(0deg) scale(0.96); opacity: 1; }
  15%  { transform: translate(0, 0) rotate(0deg) scale(0.94); opacity: 1; }
  60%  { transform: translate(0, var(--cp-tut-drag)) rotate(0deg) scale(0.98); opacity: 1; }
  66%  { transform: translate(2px, calc(var(--cp-tut-drag) + 10px)) rotate(2deg) scale(1.08); opacity: 0.9; }
  72%  { transform: translate(4px, calc(var(--cp-tut-drag) + 32px)) rotate(4deg) scale(1.14); opacity: 0; }
  100% { transform: translate(-18px, -90px) rotate(-6deg) scale(1.02); opacity: 0; }
}

@keyframes cp-tut-joy-outer {
  0%,10%  { opacity: 0; transform: scale(0.5); }
  14%     { opacity: 0.95; transform: scale(0.7); }
  60%     { opacity: 1; transform: scale(1); }
  68%     { opacity: 0.6; transform: scale(1.05); }
  74%,100%{ opacity: 0; transform: scale(1.05); }
}

@keyframes cp-tut-joy-ripple {
  0%,10%  { opacity: 0; transform: scale(0.4); }
  13%     { opacity: 0.9; transform: scale(0.6); }
  30%     { opacity: 0; transform: scale(3.2); }
  100%    { opacity: 0; transform: scale(3.2); }
}

@keyframes cp-tut-joy-knob {
  0%,14%  { opacity: 0; transform: translate(0, 0); }
  18%     { opacity: 0.95; transform: translate(0, 8px); }
  60%     { opacity: 0.95; transform: translate(0, var(--cp-tut-drag)); }
  68%     { opacity: 0.5; transform: translate(0, var(--cp-tut-drag)); }
  72%,100%{ opacity: 0; transform: translate(0, var(--cp-tut-drag)); }
}

@keyframes cp-tut-joy-line {
  0%,14%  { opacity: 0; transform: scaleY(0); }
  18%     { opacity: 0.55; transform: scaleY(0.08); }
  60%     { opacity: 0.8; transform: scaleY(1); }
  68%     { opacity: 0.4; transform: scaleY(1); }
  72%,100%{ opacity: 0; transform: scaleY(1); }
}

@keyframes cp-tut-power {
  0%,10%  { opacity: 0; transform: scaleX(0); }
  14%     { opacity: 1; transform: scaleX(0); }
  60%     { opacity: 1; transform: scaleX(0.85); }
  64%     { opacity: 1; transform: scaleX(1); }
  72%     { opacity: 0; transform: scaleX(1); }
  100%    { opacity: 0; transform: scaleX(0); }
}

@keyframes cp-tut-shot-burst {
  0%,60%  { opacity: 0; transform: translate(0, 0) scale(0.4); }
  62%     { opacity: 1; transform: translate(0, -4px) scale(1.2); }
  70%     { opacity: 0.9; transform: translate(0, -60px) scale(0.9); }
  78%     { opacity: 0; transform: translate(0, -120px) scale(0.5); }
  100%    { opacity: 0; transform: translate(0, -120px) scale(0.5); }
}

@keyframes cp-tut-text {
  0%, 100% { opacity: 0.6; }
  30%, 72% { opacity: 1; }
}

/* Mobile / coarse-pointer: smaller joystick footprint, shorter drag,
   smaller knob — mirrors the real joystick's mobile sizing. */
@media (pointer: coarse), (max-width: 520px) {
  .cp-tutorial {
    --cp-tut-drag: 100px;
    --cp-tut-outer-size: 200px;
    --cp-tut-knob-size: 56px;
  }
  .cp-tutorial__stage { top: 42%; }
  .cp-tutorial__hand { width: 48px; height: 60px; }
  .cp-tutorial__text {
    font-size: 10px;
    letter-spacing: 0.2em;
    padding: 7px 14px;
  }
}

/* Reduced motion: freeze one readable frame — mid-drag, joystick at
   ~70% extension, power bar at ~70% fill, hand at bottom of drag. */
@media (prefers-reduced-motion: reduce) {
  .cp-tutorial,
  .cp-tutorial--on { transition: none; }

  .cp-tutorial__joy-outer {
    animation: none;
    opacity: 1;
    transform: scale(0.92);
  }
  .cp-tutorial__joy-ripple { animation: none; opacity: 0; }
  .cp-tutorial__joy-knob {
    animation: none;
    opacity: 0.95;
    transform: translate(0, calc(var(--cp-tut-drag) * 0.75));
  }
  .cp-tutorial__joy-line {
    animation: none;
    opacity: 0.75;
    transform: scaleY(0.75);
  }
  .cp-tutorial__hand {
    animation: none;
    opacity: 1;
    transform: translate(0, calc(var(--cp-tut-drag) * 0.75)) rotate(0deg) scale(0.98);
  }
  .cp-tutorial__shot-burst { animation: none; opacity: 0; }
  .cp-tutorial__power-fill {
    animation: none;
    opacity: 1;
    transform: scaleX(0.7);
  }
  .cp-tutorial__text { animation: none; opacity: 1; }
}
`;
